import { AxiosResponse } from 'axios';
import qs from 'qs';
import { ResultRs } from '@domain/rs/common/SuccessRs';
import { axios } from '@repository/RequestClient';
import PooledCustomQuestionCreateRq from '@domain/rq/pass/interview/PooledCustomQuestionCreateRq';
import { CommonQuestionListRs } from '@domain/rs/pass/interview/question/CommonQuestionRs';
import { CompanyQuestionListRs } from '@domain/rs/pass/interview/question/CompanyQuestionRs';
import { CompanySearchRs } from '@domain/rs/pass/interview/company/CompanySearchRs';
import { CustomQuestionListRs } from '@domain/rs/pass/interview/question/CustomQuestionRs';
import { PooledQuestionPolicesRs } from '@domain/rs/pass/interview/policies/PooledQuestionPolicesRs';
import { PooledAccQuestionListRs } from '@domain/rs/pass/interview/question/PooledAccQuestionListRs';
import { JobGroupCode } from '@domain/constant/filterType';
import { JobTypeCode } from '@domain/constant/v1/jobType';

const interviewPracticesPooledQuestionsPrefix = '/interview-practices/pooled-questions';

export default interface InterviewPracticesPooledQuestionsRepo {
  // 빈출 질문 목록 조회
  fetchFrequencyQuestionList(): Promise<AxiosResponse<CommonQuestionListRs>>;

  // 기업별 질문 목록 조회
  fetchCompanyQuestionList(
    companySn: number,
    jobTypeCodes?: JobTypeCode[],
    jobGroupCodes?: JobGroupCode[],
  ): Promise<AxiosResponse<CompanyQuestionListRs>>;

  // 기업 리스트 조회
  fetchCompanyList(keyword?: string): Promise<AxiosResponse<CompanySearchRs[]>>;

  // 내가 만든 질문 목록 조회
  fetchCustomQuestionList(page?: number, size?: number): Promise<AxiosResponse<CustomQuestionListRs>>;

  // 내가 만든 질문 생성
  createCustomQuestion(rq: PooledCustomQuestionCreateRq): Promise<AxiosResponse<ResultRs>>;

  // 내가 만든 질문 삭제
  deleteCustomQuestion(questionSn: number): Promise<AxiosResponse<ResultRs>>;

  // 질문 설정 정책 조회
  fetchPolicies(): Promise<AxiosResponse<PooledQuestionPolicesRs>>;

  //역량검사 결과 기반 질문 조회
  fetchAccPracticeQuestions(code: string): Promise<AxiosResponse<PooledAccQuestionListRs>>;
}

export class RemoteInterviewPracticesPooledQuestionsRepo implements InterviewPracticesPooledQuestionsRepo {
  fetchFrequencyQuestionList(): Promise<AxiosResponse<CommonQuestionListRs>> {
    return axios.get<CommonQuestionListRs>(`${interviewPracticesPooledQuestionsPrefix}/commons`);
  }

  fetchCompanyQuestionList(
    companySn: number,
    jobTypeCodes?: JobTypeCode[],
    jobGroupCodes?: JobGroupCode[],
  ): Promise<AxiosResponse<CompanyQuestionListRs>> {
    return axios.get<CompanyQuestionListRs>(`${interviewPracticesPooledQuestionsPrefix}/companies/${companySn}`, {
      params: { jobTypeCodes, jobGroupCodes },
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    });
  }

  fetchCompanyList(keyword?: string): Promise<AxiosResponse<CompanySearchRs[]>> {
    return axios.get<CompanySearchRs[]>(`${interviewPracticesPooledQuestionsPrefix}/companies/list`, {
      params: { keyword },
    });
  }

  fetchCustomQuestionList(page?: number, size?: number): Promise<AxiosResponse<CustomQuestionListRs>> {
    return axios.get<CustomQuestionListRs>(`${interviewPracticesPooledQuestionsPrefix}/customs`, {
      params: { page, size },
    });
  }

  createCustomQuestion(rq: PooledCustomQuestionCreateRq): Promise<AxiosResponse<ResultRs>> {
    return axios.post<ResultRs>(`${interviewPracticesPooledQuestionsPrefix}/customs`, rq);
  }

  deleteCustomQuestion(questionSn: number): Promise<AxiosResponse<ResultRs>> {
    return axios.delete<ResultRs>(`${interviewPracticesPooledQuestionsPrefix}/customs/${questionSn}`);
  }

  fetchPolicies(): Promise<AxiosResponse<PooledQuestionPolicesRs>> {
    return axios.get<PooledQuestionPolicesRs>(`${interviewPracticesPooledQuestionsPrefix}/policies`);
  }

  fetchAccPracticeQuestions(code: string): Promise<AxiosResponse<PooledAccQuestionListRs>> {
    return axios.get(`/interview-practices/pooled-questions/acc/${code}`);
  }
}
